import { useMemo, useState } from 'react';
import { Landmark } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import type { BankConnectionLinkResult } from '@signalvote/shared';
import { useCampaignStore } from '@/stores';
import {
  useCreateBankLinkToken,
  useExchangePublicToken,
} from '@/hooks/queries/useBankConnections';
import { cn } from '@/lib/utils';
import { PlaidLinkOpener } from './PlaidLinkOpener';
import { BankAccountSelection } from './BankAccountSelection';
import {
  savePlaidLinkSession,
  clearPlaidLinkSession,
  resumePlaidLink,
} from './plaidLinkSession';

/**
 * The guided "Link a bank" flow (ADR-070, #562) on the Banks & processors setup
 * page. Gated by Finance:Manage — linking a bank is a treasurer-grade action —
 * so it self-hides for anyone below that tier.
 *
 * Flow: click → mint a Link token → open Plaid Link (SDK overlay) → exchange the
 * `public_token` → pick which accounts to track. The first pull runs at
 * selection and its rows land in the EXISTING triage inbox — this flow builds no
 * inbox of its own, and CSV/OFX upload stays available on the Sources page as
 * the always-there fallback.
 */
export function LinkBankFlow() {
  const canManage = useCampaignStore((s) => s.canManage('Finance'));

  const { mutateAsync: createLinkToken, isPending: isCreating } = useCreateBankLinkToken();
  const { mutateAsync: exchangePublicToken, isPending: isExchanging } = useExchangePublicToken();

  // On an OAuth return (#621), an OAuth bank redirected the treasurer back to this
  // page (carrying `oauth_state_id`) after a full navigation that wiped all state.
  // Restore the `link_token` we stashed before the hand-off and resume Link with
  // `receivedRedirectUri` set. Computed once from storage — a normal (non-OAuth)
  // mount yields null. `null` is the ordinary idle entry point.
  const oauthResume = useMemo(() => resumePlaidLink('link'), []);

  // `linkToken` present ⇒ Plaid Link is open. `exchange` present ⇒ we're on the
  // account-selection step. Both null ⇒ the idle entry point.
  const [linkToken, setLinkToken] = useState<string | null>(oauthResume?.token ?? null);
  // Set ONLY for the OAuth-resumed session — a fresh open (below) clears it.
  const [receivedRedirectUri, setReceivedRedirectUri] = useState<string | null>(
    oauthResume?.receivedRedirectUri ?? null,
  );
  const [exchange, setExchange] = useState<BankConnectionLinkResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!canManage) return null;

  const startLink = async () => {
    setError(null);
    setExchange(null);
    setLinkToken(null);
    setReceivedRedirectUri(null); // a fresh open is not an OAuth resume
    try {
      const result = await createLinkToken();
      // Persist BEFORE opening Link so an OAuth hand-off (Chase/BofA) survives the
      // navigation to the bank and back.
      savePlaidLinkSession({ token: result.linkToken, flow: 'link' });
      setLinkToken(result.linkToken);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start bank linking.');
    }
  };

  const handlePublicToken = async (publicToken: string) => {
    // Close Link before the network hop so the overlay doesn't linger, and drop the
    // stashed session — the link is finished, so it must never resume again.
    setLinkToken(null);
    setReceivedRedirectUri(null);
    clearPlaidLinkSession();
    setError(null);
    try {
      const result = await exchangePublicToken(publicToken);
      setExchange(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not link the bank.');
    }
  };

  // Treasurer closed Link (or it errored) without finishing — back to idle, and
  // clear the dead session so a later attempt never resumes it.
  const handleExit = () => {
    setLinkToken(null);
    setReceivedRedirectUri(null);
    clearPlaidLinkSession();
  };

  const reset = () => {
    setLinkToken(null);
    setReceivedRedirectUri(null);
    setExchange(null);
    setError(null);
  };

  if (exchange) {
    return <BankAccountSelection exchange={exchange} onReset={reset} />;
  }

  const busy = isCreating || isExchanging;

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-foreground">
          Link a bank automatically
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Connect your bank securely through Plaid and your transactions flow
          straight into your triage inbox — no downloading or uploading files by
          hand. You&apos;ll choose which accounts to track. Prefer to do it
          yourself? You can still upload CSV or OFX files anytime from the{' '}
          <Link
            to="/admin/finance/settings/sources"
            className="underline font-medium text-foreground"
          >
            Sources
          </Link>{' '}
          tab.
        </p>
      </div>

      <button
        type="button"
        onClick={startLink}
        disabled={busy}
        className={cn(
          'inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90',
          busy && 'opacity-60 cursor-not-allowed',
        )}
      >
        <Landmark className="h-4 w-4" aria-hidden />
        {isCreating ? 'Preparing…' : isExchanging ? 'Linking your bank…' : 'Link a bank'}
      </button>

      {error && (
        <p className="text-sm text-red-600" data-testid="bank-link-error">
          {error}
        </p>
      )}

      {linkToken && (
        <PlaidLinkOpener
          token={linkToken}
          onSuccess={handlePublicToken}
          onExit={handleExit}
          receivedRedirectUri={receivedRedirectUri ?? undefined}
        />
      )}
    </section>
  );
}
